import { useState, useEffect, useCallback, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Check, CheckCircle2, Copy, Eye, EyeOff, ExternalLink, Pencil, Shuffle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import PlaceholderFillPanel, { PlaceholderOccurrence } from "@/components/PlaceholderFillPanel";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { FavoriteButton } from "@/components/shared/FavoriteButton";
import { TOOL_COLORS } from "@/lib/toolConstants";
import { cn } from "@/lib/utils";

interface ImprovedVersionCardProps {
  rewriteTagged: string;
  rewriteClean: string;
  improvements?: string[];
  rewriteSummary?: string;
  scoreId?: string;
  title?: string;
  showFavorite?: boolean;
  contentHeightClassName?: string;
  promptPlaceholder?: string;
  workedLabel?: string;
  onPromptChange?: (newPrompt: string) => void;
  onCleanChange?: (newClean: string) => void;
  onTaggedChange?: (newTagged: string) => void;
  onRemix?: () => void;
  optimizedForTool?: string | null;
  modelPicker?: ReactNode;
}

const parseTaggedText = (text: string) => {
  const parts: {
    type: "text" | "orange" | "blue";
    content: string;
  }[] = [];
  let remaining = text;
  while (remaining.length > 0) {
    const orangeStart = remaining.indexOf("[ORANGE]");
    const blueStart = remaining.indexOf("[BLUE]");
    let nextTag = -1;
    let tagType: "orange" | "blue" = "orange";
    if (orangeStart !== -1 && (blueStart === -1 || orangeStart < blueStart)) {
      nextTag = orangeStart;
      tagType = "orange";
    } else if (blueStart !== -1) {
      nextTag = blueStart;
      tagType = "blue";
    }
    if (nextTag === -1) {
      if (remaining) parts.push({
        type: "text",
        content: remaining
      });
      break;
    }
    if (nextTag > 0) {
      parts.push({
        type: "text",
        content: remaining.slice(0, nextTag)
      });
    }
    const openTag = tagType === "orange" ? "[ORANGE]" : "[BLUE]";
    const closeTag = tagType === "orange" ? "[/ORANGE]" : "[/BLUE]";
    const contentStart = nextTag + openTag.length;
    const contentEnd = remaining.indexOf(closeTag, contentStart);
    if (contentEnd === -1) {
      parts.push({
        type: "text",
        content: remaining.slice(nextTag)
      });
      break;
    }
    parts.push({
      type: tagType,
      content: remaining.slice(contentStart, contentEnd)
    });
    remaining = remaining.slice(contentEnd + closeTag.length);
  }
  return parts;
};

interface PlaceholderMatch {
  id: string;
  placeholder: string;
  startIndex: number;
}

const extractPlaceholderMatches = (text: string): PlaceholderMatch[] => {
  const regex = /\[[^\]]{2,120}\]/g;
  const matches: PlaceholderMatch[] = [];
  let match;
  while ((match = regex.exec(text)) !== null) {
    const placeholder = match[0];
    if (/[a-zA-Z0-9]/.test(placeholder)) {
      matches.push({
        id: `placeholder-${match.index}`,
        placeholder,
        startIndex: match.index
      });
    }
  }
  return matches;
};

const renderWithPlaceholders = (text: string, baseKey: string, placeholderMatches?: PlaceholderMatch[]) => {
  const placeholderRegex = /\[[^\]]{2,120}\]/g;
  const result: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;
  let matchIndex = 0;
  while ((match = placeholderRegex.exec(text)) !== null) {
    const placeholder = match[0];
    if (/[a-zA-Z0-9]/.test(placeholder)) {
      if (match.index > lastIndex) {
        result.push(<span key={`${baseKey}-text-${lastIndex}`}>{text.slice(lastIndex, match.index)}</span>);
      }
      const placeholderId = placeholderMatches?.[matchIndex]?.id || `${baseKey}-placeholder-${match.index}`;
      result.push(
        <span key={`${baseKey}-placeholder-${match.index}`} className="highlight-yellow" data-placeholder-id={placeholderId}>
          {placeholder}
        </span>
      );
      lastIndex = match.index + placeholder.length;
      matchIndex++;
    }
  }
  if (lastIndex < text.length) {
    result.push(<span key={`${baseKey}-text-${lastIndex}`}>{text.slice(lastIndex)}</span>);
  }
  return result.length > 0 ? result : text;
};

export function ImprovedVersionCard({
  rewriteTagged,
  rewriteClean,
  improvements,
  rewriteSummary,
  scoreId,
  title = "Prompt",
  showFavorite = true,
  contentHeightClassName,
  promptPlaceholder = "Edit your improved prompt...",
  workedLabel = "It Worked",
  onPromptChange,
  onCleanChange,
  onTaggedChange,
  onRemix,
  optimizedForTool,
  modelPicker,
}: ImprovedVersionCardProps) {
  const { user } = useAuth();
  const [showChanges, setShowChanges] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [hasWorked, setHasWorked] = useState(false);
  
  const [localClean, setLocalClean] = useState(rewriteClean ?? '');
  const [localTagged, setLocalTagged] = useState(rewriteTagged);
  const [originalCleanForReset] = useState(rewriteClean);

  const trackAction = useCallback(async (actionType: 'copy' | 'share_chatgpt' | 'worked') => {
    if (!user || !scoreId) return;
    try {
      await supabase.from('prompt_actions').insert({
        score_id: scoreId,
        user_id: user.id,
        action_type: actionType,
      });
    } catch (err) {
      console.error('Failed to track action:', err);
    }
  }, [user, scoreId]);

  useEffect(() => {
    setLocalClean(rewriteClean);
    setLocalTagged(rewriteTagged);
  }, [rewriteClean, rewriteTagged]);

  const handleEditToggle = useCallback(() => {
    if (isEditing) {
      onCleanChange?.(localClean);
      onTaggedChange?.(localTagged);
      onPromptChange?.(localClean);
    }
    setIsEditing(!isEditing);
  }, [isEditing, localClean, localTagged, onPromptChange, onCleanChange, onTaggedChange]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(localClean);
    setCopied(true);
    toast.success("Copied to clipboard!");
    trackAction('copy');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = () => {
    trackAction('share_chatgpt');
  };

  const handleWorked = () => {
    if (hasWorked) return;
    setHasWorked(true);
    trackAction('worked');
    toast.success("Marked as worked");
  };

  const chatGptUrl = `https://chatgpt.com/?prompt=${encodeURIComponent(localClean)}`;

  const handleApplyField = useCallback((occurrence: PlaceholderOccurrence, value: string, oldValue?: string) => {
    const replaceOccurrence = (text: string): string => {
      if (oldValue) {
        const searchPattern = occurrence.contextBefore + oldValue + occurrence.contextAfter;
        const fullMatch = text.indexOf(searchPattern);
        if (fullMatch !== -1) {
          const valueStart = fullMatch + occurrence.contextBefore.length;
          const valueEnd = valueStart + oldValue.length;
          return text.slice(0, valueStart) + value + text.slice(valueEnd);
        }
        const approxStart = Math.max(0, occurrence.startIndex - 50);
        const approxEnd = Math.min(text.length, occurrence.startIndex + oldValue.length + 50);
        const searchRegion = text.slice(approxStart, approxEnd);
        const oldValueIndex = searchRegion.indexOf(oldValue);
        if (oldValueIndex !== -1) {
          const absoluteIndex = approxStart + oldValueIndex;
          return text.slice(0, absoluteIndex) + value + text.slice(absoluteIndex + oldValue.length);
        }
        return text.split(oldValue).join(value);
      } else {
        const searchPattern = occurrence.contextBefore + occurrence.placeholderText + occurrence.contextAfter;
        const fullMatch = text.indexOf(searchPattern);
        if (fullMatch !== -1) {
          const placeholderStart = fullMatch + occurrence.contextBefore.length;
          const placeholderEnd = placeholderStart + occurrence.placeholderText.length;
          return text.slice(0, placeholderStart) + value + text.slice(placeholderEnd);
        }
        const approxStart = Math.max(0, occurrence.startIndex - 30);
        const approxEnd = Math.min(text.length, occurrence.startIndex + occurrence.placeholderText.length + 30);
        const searchRegion = text.slice(approxStart, approxEnd);
        const placeholderIndex = searchRegion.indexOf(occurrence.placeholderText);
        if (placeholderIndex !== -1) {
          const absoluteIndex = approxStart + placeholderIndex;
          return text.slice(0, absoluteIndex) + value + text.slice(absoluteIndex + occurrence.placeholderText.length);
        }
        const globalIndex = text.indexOf(occurrence.placeholderText);
        if (globalIndex !== -1) {
          return text.slice(0, globalIndex) + value + text.slice(globalIndex + occurrence.placeholderText.length);
        }
        return text;
      }
    };
    
    const newClean = replaceOccurrence(localClean);
    const newTagged = replaceOccurrence(localTagged);
    
    setLocalClean(newClean);
    setLocalTagged(newTagged);
    
    onCleanChange?.(newClean);
    onTaggedChange?.(newTagged);
  }, [localClean, localTagged, onCleanChange, onTaggedChange]);

  const handleScrollToPlaceholder = useCallback((placeholderId: string) => {
    setTimeout(() => {
      const el = document.querySelector(`[data-placeholder-id="${placeholderId}"]`);
      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "center"
        });
      }
    }, 150);
  }, []);

  const parsedText = parseTaggedText(localTagged);
  const placeholderMatches = extractPlaceholderMatches(localClean);

  return (
    <>
      <Card className="bg-card border border-border rounded-2xl p-4 md:p-6 fade-in-up opacity-0 stagger-1">
        {/* Header */}
        <div className="mb-4 flex items-center gap-3">
          <div className="flex min-w-0 flex-1 items-center gap-3 overflow-x-auto">
            <div className="flex shrink-0 items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-success" />
              <h3 className="font-display font-semibold text-foreground whitespace-nowrap">{title}</h3>
            </div>

            <TooltipProvider delayDuration={300}>
              <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="subtle"
                      size="sm"
                      onClick={() => setIsPanelOpen(true)}
                      className="gap-2 border-2 text-[hsl(var(--highlight-yellow))] border-[hsl(var(--highlight-yellow)/0.6)] hover:bg-[hsl(var(--highlight-yellow)/0.1)]"
                    >
                      <span className="font-mono text-xs">[ ]</span>
                      <span className="hidden md:inline">Add Context</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Fill in placeholders</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="subtle"
                      size="sm"
                      onClick={handleEditToggle}
                      className={`gap-2 ${isEditing ? 'border-2 border-success text-success' : ''}`}
                    >
                      {isEditing ? <Check className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
                      <span className="hidden md:inline">{isEditing ? "Done Editing" : "Edit"}</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{isEditing ? "Done editing" : "Edit prompt"}</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="subtle"
                      size="sm"
                      onClick={() => setShowChanges(!showChanges)}
                      className="gap-2"
                      disabled={isEditing}
                    >
                      {showChanges ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      <span className="hidden md:inline">{showChanges ? "Hide changes" : "Show changes"}</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{showChanges ? "Hide changes" : "Show changes"}</TooltipContent>
                </Tooltip>
              </div>
            </TooltipProvider>
          </div>

          <div className="shrink-0">
            {modelPicker || (optimizedForTool && (
              <div className="flex h-9 items-center gap-2 rounded-md border border-border/70 bg-muted/20 px-3 text-xs text-muted-foreground">
                <span className="hidden sm:inline">Optimized for:</span>
                <span className={cn(
                  "rounded-full border px-2 py-0.5 font-medium text-foreground",
                  TOOL_COLORS[optimizedForTool] || "border-border bg-muted/20"
                )}>
                  {optimizedForTool}
                </span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Rewrite Summary */}
        {rewriteSummary && (
          <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
            {rewriteSummary}
          </p>
        )}
        {/* Prompt Content */}
        <div className={cn(
          "overflow-y-auto rounded-xl border border-border/60 bg-muted/20 p-4 shadow-inner",
          contentHeightClassName || "h-[340px] md:h-[420px]"
        )}>
          {isEditing ? (
            <Textarea 
              value={localClean} 
              onChange={e => setLocalClean(e.target.value)} 
              className="min-h-full text-foreground leading-relaxed resize-none bg-transparent border-0 p-0 focus-visible:ring-0 focus-visible:ring-offset-0" 
              placeholder={promptPlaceholder}
            />
          ) : showChanges ? (
            <p className="text-foreground leading-relaxed whitespace-pre-wrap">
              {parsedText.map((part, i) => {
                if (part.type === "orange") {
                  return (
                    <span key={i} className="highlight-orange">
                      {renderWithPlaceholders(part.content, `orange-${i}`, placeholderMatches)}
                    </span>
                  );
                } else if (part.type === "blue") {
                  return (
                    <span key={i} className="highlight-blue">
                      {renderWithPlaceholders(part.content, `blue-${i}`, placeholderMatches)}
                    </span>
                  );
                }
                return <span key={i}>{renderWithPlaceholders(part.content, `text-${i}`, placeholderMatches)}</span>;
              })}
            </p>
          ) : (
            <p className="text-foreground leading-relaxed whitespace-pre-wrap">
              {renderWithPlaceholders(localClean, "clean", placeholderMatches)}
            </p>
          )}
        </div>

        {/* Primary Actions */}
        <TooltipProvider delayDuration={300}>
          <div className="mt-4 flex flex-wrap items-center gap-1.5 sm:gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="subtle" size="sm" onClick={handleCopy} className="gap-2">
                  {copied ? <Check className="w-4 h-4 text-success" /> : <Copy className="w-4 h-4" />}
                  <span className="hidden md:inline">{copied ? "Copied!" : "Copy"}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>{copied ? "Copied!" : "Copy to clipboard"}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="subtle" size="sm" asChild className="gap-2" onClick={handleShare}>
                  <a href={chatGptUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-4 h-4" />
                    <span className="hidden md:inline">Use in ChatGPT</span>
                  </a>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Open in ChatGPT</TooltipContent>
            </Tooltip>
            {onRemix && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="subtle" size="sm" onClick={onRemix} className="gap-2">
                    <Shuffle className="w-4 h-4" />
                    <span className="hidden md:inline">Remix</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Remix this prompt</TooltipContent>
              </Tooltip>
            )}
            {showFavorite && scoreId && <FavoriteButton scoreId={scoreId} size="sm" />}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleWorked}
                  className="ml-auto gap-2 border-success/50 text-success hover:bg-success/10 hover:text-success"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  <span className="hidden md:inline">{workedLabel}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Mark this prompt as successful</TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      </Card>

      {/* Placeholder Fill Panel */}
      <PlaceholderFillPanel 
        isOpen={isPanelOpen} 
        onClose={() => setIsPanelOpen(false)} 
        improvedPrompt={localClean} 
        originalPromptForReset={originalCleanForReset} 
        onApplyField={handleApplyField} 
        onScrollToPlaceholder={handleScrollToPlaceholder} 
        fallbackSuggestions={improvements} 
      />
    </>
  );
}
